import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import { useCallback } from 'react';
import Cookies from 'universal-cookie';
import * as Yup from 'yup';

import { Maybe } from 'types/dashboard-core-gqlTypes.generated.d';
import { LoginFormValues } from 'types/dashboard.d';

import { LoginResponse } from './auth.types';
import { useLoginRedirectPathContext } from './LoginRedirectPathProvider';
import {
  numericRegex,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import { TOKEN_NAME } from 'packages/logged-out/hooks/useAuthManagement';

/**
 * set the token and refresh the page to the home page
 * @param authToken authToken
 */
export const loginAndRedirect = (
  authToken: Maybe<string>,
  redirectPath: Maybe<string>,
) => {
  // TODO: consolidate me with useCookie Hook, and manage auth cookie from single scope
  const cookies = new Cookies();
  cookies.set(TOKEN_NAME, authToken, { path: '/' });

  if (redirectPath) {
    window.location.replace(redirectPath);
  } else {
    window.location.reload();
  }
};

interface LoginErrorResponse {
  message: string;
}

export const useLoginFormManagement = () => {
  const toast = useToast();
  const { redirectPath, setRedirectPath } = useLoginRedirectPathContext();

  const handleSubmit = useCallback(
    async (formValues: LoginFormValues) => {
      try {
        const { data } = await axios.post<LoginResponse>('/login', formValues, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        loginAndRedirect(data.token, redirectPath);
        setRedirectPath(null);
      } catch (err: any) {
        const data: LoginErrorResponse = err.response.data;
        errorToastConfig.title = data.message;
        toast(errorToastConfig);
      }
    },
    [toast, redirectPath, setRedirectPath],
  );

  const formValidation = Yup.object().shape({
    email: Yup.string()
      .lowercase()
      .email('Must be a valid email')
      .required('Required'),
    password: Yup.string()
      .matches(numericRegex, 'one number required')
      .min(8, 'Minimum 8 characters required')
      .required('Required'),
  });

  const initialValues: LoginFormValues = {
    email: '',
    password: '',
  };

  return {
    handleSubmit,
    initialValues,
    formValidation,
  };
};
